// src/auth/permission-gate.tsx
import type { ReactNode } from 'react';
import { useAuthStore } from './auth-store';
import type { PermissionResults, SystemPermission } from './types';

/** Verbatim port of Flutter validatePermission(): every key must match,
 *  with optional add/edit/view/delete result constraints. */
export function evaluatePermissions(
  permissions: SystemPermission[] | undefined,
  keys: string[],
  check: PermissionResults = {},
): boolean {
  if (!permissions || permissions.length === 0) return false;
  return keys.every((key) =>
    permissions.some((p) => {
      if (p.permissionKey !== key) return false;
      const r = p.permissionResults ?? {};
      if (check.add !== undefined && (r.add ?? false) !== check.add) return false;
      if (check.edit !== undefined && (r.edit ?? false) !== check.edit) return false;
      if (check.view !== undefined && (r.view ?? false) !== check.view) return false;
      if (check.delete !== undefined && (r.delete ?? false) !== check.delete) return false;
      return true;
    }),
  );
}

export function usePermission(keys: string[], check: PermissionResults = {}): boolean {
  const permissions = useAuthStore((s) => s.user?.systemPermissions);
  return evaluatePermissions(permissions, keys, check);
}

interface PermissionGateProps extends PermissionResults {
  keys: string[];
  children: ReactNode;
  fallback?: ReactNode;
}

export function PermissionGate({
  keys,
  children,
  fallback = null,
  ...check
}: PermissionGateProps) {
  const allowed = usePermission(keys, check);
  return <>{allowed ? children : fallback}</>;
}
